import { UNSEATED_GROUP_ID } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import {
    CompressedPlacementParams,
    ReshuffleTableParams,
    SeatingTicket,
    TablePlacementParams,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';
import {
    createSeatNumberArray,
    indexToSeatNumber,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/helpers';

type TicketUpdate = [string, SeatingTicket];

const toTicketWithNewSeat = (
    ticket: SeatingTicket,
    seatNumber: number,
    tableId: string,
): SeatingTicket => ({
    ...ticket,
    seating_group_id: tableId,
    seat_number: seatNumber,
});

const isInUnseated = (ticket: SeatingTicket): boolean =>
    ticket.seating_group_id === UNSEATED_GROUP_ID;

const bySeatNumber = (a: SeatingTicket, b: SeatingTicket): number =>
    (a.seat_number || 0) - (b.seat_number || 0);

const toAdjustedTargetSeat = (
    requestedSeat: number,
    incomingCount: number,
    capacity: number,
): number => {
    const lastSeatNeeded = requestedSeat + incomingCount - 1;
    if (lastSeatNeeded > capacity) {
        const adjustment = lastSeatNeeded - capacity;
        return Math.max(1, requestedSeat - adjustment);
    }
    return requestedSeat;
};

const toCompressedPlacements = (
    params: CompressedPlacementParams,
): Array<{ ticketId: string; seatNumber: number }> => {
    const { existingAtTable, incomingTicketIds, adjustedTargetSeat, tableCapacity } = params;
    const incomingCount = incomingTicketIds.length;

    const existingSorted = existingAtTable
        .filter(t => t.seat_number && t.seat_number > 0)
        .sort(bySeatNumber);

    const incomingPlacements = incomingTicketIds.map((id, index) => ({
        ticketId: id,
        seatNumber: adjustedTargetSeat + index,
    }));

    const incomingSeats = new Set(
        Array.from({ length: incomingCount }, (_, i) => adjustedTargetSeat + i),
    );

    const availableSeats = createSeatNumberArray(tableCapacity).filter(
        seat => !incomingSeats.has(seat),
    );

    const shiftedPlacements = findClosestAvailableSeats(existingSorted, availableSeats);

    return [...incomingPlacements, ...shiftedPlacements];
};

const isTicketBetween = (ticket: SeatingTicket, from: number, to: number): boolean => {
    const seat = ticket.seat_number || 0;
    if (from < to) {
        return seat > from && seat <= to;
    } else {
        return seat >= to && seat < from;
    }
};

const toSameTableShufflePlacements = (
    movingTicket: SeatingTicket,
    targetSeat: number,
    existingTickets: SeatingTicket[],
): Array<{ ticketId: string; seatNumber: number }> => {
    const originalSeat = movingTicket.seat_number || 0;
    if (originalSeat === targetSeat) return [];

    const shiftDirection = originalSeat < targetSeat ? -1 : 1;

    return existingTickets
        .map(ticket => {
            if (ticket.id === movingTicket.id) {
                return { ticketId: ticket.id, seatNumber: targetSeat };
            }

            if (isTicketBetween(ticket, originalSeat, targetSeat)) {
                return {
                    ticketId: ticket.id,
                    seatNumber: (ticket.seat_number || 0) + shiftDirection,
                };
            }

            return null;
        })
        .filter((p): p is { ticketId: string; seatNumber: number } => p !== null);
};

const toIncomingPlacements = (
    ticketIds: string[],
    startSeat: number,
): Array<{ ticketId: string; seatNumber: number }> =>
    ticketIds.map((id, offset) => ({
        ticketId: id,
        seatNumber: startSeat + offset,
    }));

const findAvailableSeat = (
    originalSeat: number,
    usedSeats: number[],
    tableCapacity: number,
): number | null => {
    const allSeats = createSeatNumberArray(tableCapacity);

    const seatAfterOriginal = allSeats.find(
        seat => !usedSeats.includes(seat) && seat > originalSeat,
    );

    if (seatAfterOriginal) {
        return seatAfterOriginal;
    }

    const availableSeats = allSeats.filter(seat => !usedSeats.includes(seat));

    if (availableSeats.length === 0) {
        return null;
    }

    return availableSeats.reduce((closest, seat) => {
        const closestDistance = Math.abs(closest - originalSeat);
        const seatDistance = Math.abs(seat - originalSeat);
        return seatDistance < closestDistance ? seat : closest;
    }, availableSeats[0]);
};

const findPlacementsForDisplacedTickets = (
    displacedTickets: SeatingTicket[],
    occupiedSeats: number[],
    tableCapacity: number,
): Array<{ ticketId: string; seatNumber: number }> => {
    return displacedTickets.reduce<Array<{ ticketId: string; seatNumber: number }>>(
        (acc, ticket) => {
            const originalSeat = ticket.seat_number || 0;

            const remainingDisplacedSeats = displacedTickets
                .filter(t => !acc.some(p => p.ticketId === t.id))
                .map(t => t.seat_number || 0);

            const usedSeats = [
                ...occupiedSeats,
                ...remainingDisplacedSeats,
                ...acc.map(p => p.seatNumber),
            ];
            const availableSeat = findAvailableSeat(originalSeat, usedSeats, tableCapacity);

            return availableSeat
                ? [...acc, { ticketId: ticket.id, seatNumber: availableSeat }]
                : acc;
        },
        [],
    );
};

const findClosestAvailableSeats = (
    existingTickets: SeatingTicket[],
    availableSeats: number[],
): { ticketId: string; seatNumber: number }[] => {
    return existingTickets.reduce<Array<{ ticketId: string; seatNumber: number }>>(
        (acc, ticket) => {
            const originalSeat = ticket.seat_number || 0;
            const remainingSeats = availableSeats.filter(
                seat => !acc.some(p => p.seatNumber === seat),
            );

            const closestSeat = remainingSeats.reduce((closest, seat) => {
                const currentDistance = Math.abs(originalSeat - seat);
                const closestDistance = Math.abs(originalSeat - closest);
                return currentDistance < closestDistance ? seat : closest;
            }, remainingSeats[0] || 0);

            return closestSeat > 0
                ? [...acc, { ticketId: ticket.id, seatNumber: closestSeat }]
                : acc;
        },
        [],
    );
};

const toTablePlacements = (
    params: TablePlacementParams,
): Array<{ ticketId: string; seatNumber: number }> => {
    const { existingAtTable, incomingTicketIds, adjustedTargetSeat, tableCapacity, allAtTable } =
        params;
    const tableTickets = allAtTable || existingAtTable;

    const existingSorted = tableTickets
        .filter(t => t.seat_number && t.seat_number > 0)
        .sort(bySeatNumber);

    const isSameTableMove = incomingTicketIds.length === 1 && allAtTable !== undefined;

    if (isSameTableMove) {
        const movingTicket = tableTickets.find(t => t.id === incomingTicketIds[0]);
        return movingTicket
            ? toSameTableShufflePlacements(movingTicket, adjustedTargetSeat, existingSorted)
            : [];
    }

    const incomingPlacements = toIncomingPlacements(incomingTicketIds, adjustedTargetSeat);
    const incomingEndSeat = adjustedTargetSeat + incomingTicketIds.length - 1;

    const existingOnly = existingAtTable
        .filter(t => t.seat_number && t.seat_number > 0)
        .sort(bySeatNumber);

    const ticketsToDisplace = existingOnly.filter(
        t => t.seat_number && t.seat_number >= adjustedTargetSeat,
    );

    const cascadePlacements = ticketsToDisplace
        .map((ticket, index) => ({
            ticketId: ticket.id,
            seatNumber: incomingEndSeat + indexToSeatNumber(index),
        }))
        .filter(p => p.seatNumber <= tableCapacity);

    if (cascadePlacements.length > 0 && cascadePlacements.length === ticketsToDisplace.length) {
        return [...incomingPlacements, ...cascadePlacements];
    }

    const incomingSeats = new Set(incomingPlacements.map(p => p.seatNumber));
    const displacedTickets = existingOnly.filter(
        t => t.seat_number && incomingSeats.has(t.seat_number),
    );

    const preservedTickets = existingOnly.filter(
        t =>
            t.seat_number &&
            !incomingSeats.has(t.seat_number) &&
            !displacedTickets.some(d => d.id === t.id),
    );

    const occupiedSeats = [...incomingSeats, ...preservedTickets.map(t => t.seat_number || 0)];
    const displacedPlacements = findPlacementsForDisplacedTickets(
        displacedTickets,
        occupiedSeats,
        tableCapacity,
    );

    return [...incomingPlacements, ...displacedPlacements];
};

const toTableUpdate = (
    placement: { ticketId: string; seatNumber: number },
    tickets: Record<string, SeatingTicket>,
    tableId: string,
): TicketUpdate => {
    const ticket = tickets[placement.ticketId];
    const updatedTicket = toTicketWithNewSeat(ticket, placement.seatNumber, tableId);
    return [placement.ticketId, updatedTicket];
};

const toCompactedUnseatedSeats = (tickets: Record<string, SeatingTicket>): TicketUpdate[] => {
    const unseatedTickets = Object.values(tickets).filter(isInUnseated).sort(bySeatNumber);

    return unseatedTickets
        .map((ticket, index): TicketUpdate | null => {
            const expectedSeat = indexToSeatNumber(index);
            return ticket.seat_number !== expectedSeat
                ? [ticket.id, toTicketWithNewSeat(ticket, expectedSeat, UNSEATED_GROUP_ID)]
                : null;
        })
        .filter((update): update is TicketUpdate => update !== null);
};

const reshuffleTable = (params: ReshuffleTableParams): Record<string, SeatingTicket> => {
    const { incomingTicketIds, tableId, targetSeatNumber, tickets, tableCapacity } = params;

    if (incomingTicketIds.length === 0 || tableCapacity < 1) return {};

    const adjustedTargetSeat = toAdjustedTargetSeat(
        targetSeatNumber,
        incomingTicketIds.length,
        tableCapacity,
    );

    const allAtTable = Object.values(tickets).filter(t => t.seating_group_id === tableId);

    const isSameTableMove =
        incomingTicketIds.length === 1 &&
        incomingTicketIds.every(id => {
            const ticket = tickets[id];
            return (
                ticket &&
                ticket.seating_group_id === tableId &&
                ticket.seating_group_id !== UNSEATED_GROUP_ID
            );
        });

    const existingAtTable = allAtTable.filter(t => !incomingTicketIds.includes(t.id));

    const wasAdjustedUp = adjustedTargetSeat < targetSeatNumber;

    const placements = wasAdjustedUp
        ? toCompressedPlacements({
              existingAtTable,
              incomingTicketIds,
              adjustedTargetSeat,
              tableCapacity,
          })
        : toTablePlacements({
              existingAtTable,
              incomingTicketIds,
              adjustedTargetSeat,
              tableCapacity,
              allAtTable: isSameTableMove ? allAtTable : undefined,
          });

    const tableUpdates = placements
        .filter(p => {
            const originalTicket = tickets[p.ticketId];
            return (
                !originalTicket ||
                originalTicket.seating_group_id !== tableId ||
                originalTicket.seat_number !== p.seatNumber
            );
        })
        .map(p => toTableUpdate(p, tickets, tableId));

    const movingFromUnseated = incomingTicketIds.some(
        id => tickets[id] && isInUnseated(tickets[id]),
    );

    if (!movingFromUnseated) {
        return Object.fromEntries(tableUpdates);
    }

    const ticketsAfterTable = { ...tickets, ...Object.fromEntries(tableUpdates) };
    const unseatedUpdates = toCompactedUnseatedSeats(ticketsAfterTable);

    return Object.fromEntries([...tableUpdates, ...unseatedUpdates]);
};

export { reshuffleTable };
